import { ThunderboltFilled } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import {
  Alert,
  Button,
  Card,
  ConfigProvider,
  Divider,
  Flex,
  Form,
  Input,
  Layout,
  Typography,
} from "antd";
import { Content } from "antd/es/layout/layout";
import React, { useState } from "react";
import { connect } from "react-redux";

import { silentClient } from "../api-client";
import type { RootState } from "../store";
import { loginTheme } from "../theme";
import styles from "./SetPasswordPage.module.css";

type SetPasswordFieldType = {
  password: string;
  confirmPassword: string;
};

type Props = {
  secret: string | undefined;
  intl: IntlShape;
};

type SetPasswordFailure = {
  response?: {
    status?: number;
    data?:
      | {
          error?: {
            field_validation?: { password?: string[] };
          };
        }
      | string;
  };
  code?: string;
};

const SetPasswordPage: React.FC<Props> = ({ secret, intl }) => {
  const [form] = Form.useForm<SetPasswordFieldType>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessageKey, setErrorMessageKey] = useState<string | null>(null);
  const [isLinkInvalid, setIsLinkInvalid] = useState(!secret);

  const handleFailure = (errorLike: unknown) => {
    const failure = errorLike as SetPasswordFailure;
    const failureData = failure?.response?.data;
    if (failure?.code === "ERR_NETWORK") {
      setErrorMessageKey("login_error_network");
    } else if (failureData === "link expired") {
      setIsLinkInvalid(true);
    } else if (
      typeof failureData === "object" &&
      failureData?.error?.field_validation?.password
    ) {
      form.setFields([
        {
          name: "password",
          errors: failureData.error.field_validation.password,
        },
      ]);
    } else {
      setErrorMessageKey("set_password_error_generic");
    }
  };

  const handleSubmit = async (values: SetPasswordFieldType) => {
    setIsSubmitting(true);
    setErrorMessageKey(null);
    try {
      const response = await silentClient.apiV1ManagementLoginSetPasswordCreate(
        { password: values.password },
        secret
      );
      if (response?.status === 200) {
        window.location.replace("/login");
      } else {
        handleFailure(response);
      }
    } catch (error: unknown) {
      handleFailure(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCardContent = () => {
    if (isLinkInvalid) {
      return (
        <Alert
          type="error"
          title={intl.formatMessage({ id: "set_password_invalid_link_title" })}
          description={intl.formatMessage({ id: "set_password_invalid_link" })}
          showIcon
        />
      );
    }

    return (
      <>
        <Typography.Title level={4} className={styles.heading}>
          {intl.formatMessage({ id: "set_password_header" })}
        </Typography.Title>
        <Typography.Text className={styles.subHeading}>
          {intl.formatMessage({ id: "set_password_subheader" })}
        </Typography.Text>

        {errorMessageKey && (
          <Alert
            type="error"
            title={intl.formatMessage({ id: errorMessageKey })}
            className={styles.errorAlert}
            showIcon
          />
        )}

        <Form
          form={form}
          name="setPassword"
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
          className={styles.form}
        >
          <Form.Item<SetPasswordFieldType>
            label={
              <Typography.Text className={styles.label}>
                {intl.formatMessage({ id: "set_password_field_password" })}
              </Typography.Text>
            }
            name="password"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "set_password_input_password_missing",
                }),
              },
            ]}
          >
            <Input.Password size="large" placeholder="••••••••" />
          </Form.Item>

          <Form.Item<SetPasswordFieldType>
            label={
              <Typography.Text className={styles.label}>
                {intl.formatMessage({ id: "set_password_field_confirm" })}
              </Typography.Text>
            }
            name="confirmPassword"
            dependencies={["password"]}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "set_password_input_confirm_missing",
                }),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(intl.formatMessage({ id: "set_password_error_mismatch" }))
                  );
                },
              }),
            ]}
          >
            <Input.Password size="large" placeholder="••••••••" />
          </Form.Item>

          <Form.Item label={null} className={styles.submitFormItem}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={isSubmitting}
              disabled={isSubmitting}
              style={{ fontWeight: 600 }}
            >
              {intl.formatMessage({ id: "set_password_button_submit" })}
            </Button>
          </Form.Item>
        </Form>
      </>
    );
  };

  return (
    <ConfigProvider theme={loginTheme}>
      <Layout>
        <Content>
          <Flex vertical className={styles.pageWrapper} align="center" justify="center">
            <Card className={styles.card}>
              <Flex align="center" gap={12} className={styles.brand}>
                <Flex align="center" justify="center" className={styles.iconWrap}>
                  <ThunderboltFilled className={styles.brandIcon} />
                </Flex>
                <Flex vertical>
                  <Typography.Text className={styles.brandName}>
                    DZ Projects
                  </Typography.Text>
                  <Typography.Text className={styles.brandTag}>
                    Wind Asset Management
                  </Typography.Text>
                </Flex>
              </Flex>

              <Divider className={styles.divider} />

              {renderCardContent()}
            </Card>
          </Flex>
        </Content>
      </Layout>
    </ConfigProvider>
  );
};

export const ConnectedSetPasswordPage = connect((state: RootState) => ({
  secret: state.router.route?.params?.secret,
  intl: state.intl.datetimeIntl,
}))(SetPasswordPage);

export default ConnectedSetPasswordPage;
